import React, { useState, useEffect } from 'react';
import { Inbox, Folder } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export default function MoveLessonDialog({ lesson, folders, onClose, onMove }) {
  const [selected, setSelected] = useState(null);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (lesson) setSelected(lesson.folder_id || 'root');
  }, [lesson]);

  const handleMove = async () => {
    setMoving(true);
    try {
      // "root" sentinel means uncategorized (backend accepts "" to clear)
      await onMove(selected === 'root' ? '' : selected);
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={!!lesson} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[80vh]" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl text-[#14213D]">Move lesson</DialogTitle>
          <DialogDescription className="text-xs text-[#5A6577]">
            Move &ldquo;{lesson?.title}&rdquo; to a folder.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-64 overflow-y-auto -mx-1 px-1 py-1">
          <FolderChoice
            label="Uncategorized"
            icon={Inbox}
            color="#718096"
            selected={selected === 'root'}
            onSelect={() => setSelected('root')}
            testId="move-target-root"
          />
          {folders.map((f) => (
            <FolderChoice
              key={f.id}
              label={f.name}
              icon={Folder}
              color={f.color}
              selected={selected === f.id}
              onSelect={() => setSelected(f.id)}
              testId={`move-target-${f.id}`}
            />
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="move-cancel-btn">Cancel</Button>
          <Button
            disabled={moving || !selected}
            onClick={handleMove}
            className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
            data-testid="move-confirm-btn"
          >
            {moving ? 'Moving…' : 'Move here'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FolderChoice = ({ label, icon: Icon, color, selected, onSelect, testId }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors text-left ${
      selected
        ? 'bg-[#F9F8F4] border-[#D4AF37]/60 text-[#14213D]'
        : 'bg-white border-[#EBE7E0] hover:bg-[#F9F8F4]'
    }`}
    data-testid={testId}
  >
    <Icon className="h-4 w-4 flex-shrink-0" style={{ color }} strokeWidth={1.75} />
    <span className="text-sm font-medium truncate">{label}</span>
    {selected && (
      <span className="ml-auto text-xs font-semibold text-[#D4AF37]">Selected</span>
    )}
  </button>
);
